/* eslint-disable import/no-unresolved */
import { sortBy } from 'lodash';
import { MethodologyPSV } from 'api/models/MethodologyPSV';
import { parseDatabaseValue, roundToN } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { ALLOCATION_BACKSOLVE_WEIGHTING_ALIAS } from './constants';
import { ReverseParser, RowConfig } from './types';
import { VALUATION_APPROACH_GPC_ALIAS } from '../../constants';
import { ADJUSTMENT_TYPE_ALIAS } from '../../MarketAdjustmentTable/util/constants';
import { BackSolveColumn, BackSolveTableData } from '../../types';

function extractApproachGpc(calcValue: string, tableData: BackSolveTableData, row: any, tmpColumn: any) {
  if (calcValue) {
    const colAlias = tableData.approaches.find(
      ({ valuations_approach_gpc }) => valuations_approach_gpc?.id?.toString() === calcValue
    )
      ? 'gpc_approach'
      : 'reference_for_backsolve';
    // eslint-disable-next-line no-param-reassign
    tmpColumn[colAlias] = calcValue;
  } else {
    // eslint-disable-next-line no-param-reassign
    tmpColumn.gpc_approach = undefined;
  }
}

const reverseParseMethodologyPresentShareValues = (
  column: BackSolveColumn,
  valueDecimalPlaces: number,
  rowConfig: RowConfig[]
) => {
  if (column.present_share_values?.length > 0) {
    const relevantRowSecurityMap = rowConfig
      .filter((row: any) => row.securityName)
      .reduce(
        (sofar, next: any) => ({
          ...sofar,
          [next.customKey]: next,
        }),
        {}
      );

    let matchedPresentShareValues: Record<string, any> = {};
    const unmatchedPresentShareValues = [...(column.deleted_present_share_values ?? [])];
    column.present_share_values.forEach((psv: any) => {
      if (relevantRowSecurityMap[psv.security]) {
        matchedPresentShareValues = {
          ...matchedPresentShareValues,
          [psv.security]: psv,
        };
      } else if (psv.id) {
        unmatchedPresentShareValues.push(psv.id);
      }
    });
    const mappedPSV = Object.values(relevantRowSecurityMap).map(
      (row: any) =>
        ({
          security: row?.customKey as string,
          methodology: column.id ?? null,
          id: matchedPresentShareValues[row.customKey as keyof typeof matchedPresentShareValues]?.id ?? 0,
          value: roundToN(column[row.customKey as keyof typeof column], valueDecimalPlaces),
        } as unknown as MethodologyPSV)
    );
    // eslint-disable-next-line no-param-reassign
    column.present_share_values = mappedPSV;
    // eslint-disable-next-line no-param-reassign
    column.deleted_present_share_values = Array.from(new Set(unmatchedPresentShareValues));
  } else if (column.present_share_values?.length === 0) {
    // eslint-disable-next-line no-param-reassign
    column.present_share_values = Object.entries(column)
      .filter(pair => Number(pair[0]))
      .map(
        pair =>
          ({
            security: pair[0],
            value: roundToN(pair[1], valueDecimalPlaces),
            id: 0,
            methodology: column.id ?? null,
          } as unknown as MethodologyPSV)
      );
  }
};

const reverseParser = ({ cells, columns, allowEmptyValues, rowConfig, fieldAttributes, tableData }: ReverseParser) => {
  const sortedColumns = sortBy(columns, ['order']);
  const alphabet = alphabetGenerator([], sortedColumns.length);

  sortedColumns.forEach((column, columnIndex) => {
    const columnLegend = alphabet[columnIndex];

    // copy original values
    const tmpColumn: any = column;

    // replace updated values
    rowConfig.forEach((row, cellIndex) => {
      const key = columnLegend + (cellIndex + 1);
      const defaultValue = row.defaultValue || null;
      const type = row.dbType || null;
      const format = row.format || null;
      const value = cells[key] ? cells[key].value : defaultValue;
      const { gridType } = row;
      let decimalPlaces = fieldAttributes[row.alias]?.decimal_places as number;

      if (row.alias === ALLOCATION_BACKSOLVE_WEIGHTING_ALIAS) {
        decimalPlaces = 3;
      }

      if (row.alias && cells[key]) {
        const calcValue = parseDatabaseValue({
          type,
          value,
          format,
          allowEmptyValues,
          gridType,
          decimalPlaces,
        });

        if (row.alias === VALUATION_APPROACH_GPC_ALIAS) {
          extractApproachGpc(calcValue, tableData, row, tmpColumn);
        }

        if (row.alias === ADJUSTMENT_TYPE_ALIAS && tableData?.approach?.valuations_approach_backsolve) {
          // eslint-disable-next-line no-param-reassign
          tableData.approach.valuations_approach_backsolve.adjustment_type_selection = calcValue;
        }

        tmpColumn[row.alias as keyof typeof tmpColumn] = calcValue;
      }
    });

    reverseParseMethodologyPresentShareValues(tmpColumn, fieldAttributes.value?.decimal_places as number, rowConfig);
  });
};

export default reverseParser;
